"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function MainPostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="221771" className="mx-auto max-w-4xl animate-pulse">
        <div className="mb-6 h-12 w-3/4 rounded-sm bg-muted"></div>
        <div className="mb-8 flex items-center gap-4">
          <div className="size-12 rounded-full bg-muted"></div>
          <div className="space-y-2">
            <div className="h-4 w-32 rounded-sm bg-muted"></div>
            <div className="h-3 w-24 rounded-sm bg-muted"></div>
          </div>
        </div>
        <div className="mb-10 aspect-video w-full rounded-sm bg-muted"></div>
        <div className="space-y-4">
          <div className="h-4 w-full rounded-sm bg-muted"></div>
          <div className="h-4 w-full rounded-sm bg-muted"></div>
          <div className="h-4 w-5/6 rounded-sm bg-muted"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl py-12 text-center">
        <p className="text-destructive font-default">Error loading the project update.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl py-12 text-center">
        <p className="text-muted-foreground font-default">No project updates found at the moment.</p>
        <Link to="#" className="mt-4 inline-block">
          <Button variant="outline" className="rounded-none border-primary text-primary hover:bg-primary hover:text-white">Return to Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="mx-auto max-w-4xl">
      <header className="mb-8">
        <h1 className="font-default mb-6 text-4xl font-bold leading-tight text-foreground md:text-5xl lg:text-6xl tracking-tight">
          {post.title}
        </h1>

        <div className="flex items-center gap-4">
          <Avatar className="size-12 border border-border rounded-none">
            <AvatarImage
              src={post.author?.avatar?.["96"] || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641543/img/generic_author_avatar.png"}
              alt={post.author?.name || "Team Member"}
            />
            <AvatarFallback className="rounded-none bg-primary text-primary-foreground">{post.author?.name?.charAt(0) || "B"}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-default font-medium text-foreground">
              Published by <span className="font-bold text-primary">{post.author?.name || "Bottom Up Projects"}</span>
            </p>
            <p className="font-default text-sm text-muted-foreground">
              {post.date ? new Date(post.date).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' }) : "Date unavailable"}
            </p>
          </div>
        </div>
      </header>

      {post.featuredImage && (
        <figure className="mb-12 overflow-hidden rounded-none border-l-8 border-primary shadow-md">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="aspect-video w-full object-cover grayscale-[20%] hover:grayscale-0 transition-all duration-500"
          />
        </figure>
      )}

      <div
        className="font-default text-lg leading-relaxed text-foreground/90 [&>blockquote]:mb-6 [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:bg-muted/30 [&>blockquote]:p-6 [&>blockquote]:italic [&>h2]:mb-4 [&>h2]:mt-10 [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:text-secondary [&>h3]:mb-4 [&>h3]:mt-8 [&>h3]:text-2xl [&>h3]:font-semibold [&>h3]:text-secondary [&>img]:mb-6 [&>img]:rounded-none [&>ol]:mb-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>p]:mb-6 [&>ul]:mb-6 [&>ul]:list-disc [&>ul]:pl-6"
        dangerouslySetInnerHTML={{ __html: post.content || "<p>Project details are currently being updated.</p>" }}
      />
    </article>
  );
}

function RelatedPostsList() {
  const { posts, loading, error } = usePosts();

  if (loading || error || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="mx-auto mt-20 max-w-6xl">
      <Separator className="mb-12 bg-border" />
      <h2 className="font-default mb-8 text-3xl font-bold text-foreground">Latest Construction News</h2>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {posts.slice(0, 3).map((post, i) => (
          <Card key={post.id} data-index={i} className="flex h-full flex-col overflow-hidden border-border rounded-none bg-white transition-all duration-300 hover:shadow-lg hover:border-primary/50">
            {post.featuredImage && (
              <div className="aspect-video w-full overflow-hidden">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="size-full object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>
            )}
            <CardHeader>
              <CardTitle className="font-default line-clamp-2 text-xl font-bold text-secondary">
                {post.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-grow">
              <div
                className="font-default line-clamp-3 text-sm text-muted-foreground leading-relaxed"
                dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
              />
            </CardContent>
            <CardFooter>
              <Link to={post.link || "#"} className="w-full">
                <Button variant="outline" className="w-full rounded-none border-primary text-primary hover:bg-primary hover:text-white font-bold transition-colors">
                  Read More
                </Button>
              </Link>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : { post_type: "post", per_page: 1 };
  
  const mainPostQuery = new WP_Query(currentWPQueryParams);

  
  const relatedPostsQuery = new WP_Query({ post_type: "post", per_page: 3 });

  return (
    <section
      id="single-post-template-section"
      className="bg-background text-foreground px-4 py-16 md:px-8 lg:px-12 lg:py-24"
    >
      <div className="mx-auto max-w-7xl">
        <PostsProvider wp_query={mainPostQuery}>
          <MainPostContent />
        </PostsProvider>

        <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
          <RelatedPostsList />
        </PostsProvider>
      </div>
    </section>
  );
}